/* SPEC-545 — figure attention: which forecast figures were on screen, for how long. Pure (no React,
 * no DOM globals): hooks/useFigureAttention.ts passes the environment in, so every honesty rule is
 * testable in Node (figureAttention.test.ts).
 *
 * Param (11 Oct 2026): the planner reads figures and types them into the purchasing system or his
 * weekly spreadsheet, so nothing is clicked. This records TIME ON SCREEN — never "used" (NK-186).
 *
 * Rules:
 *   - a figure row counts only while it is on screen (the IntersectionObserver says so) AND the tab
 *     is visible AND the window is focused AND the person moved, typed, scrolled or touched within
 *     the last 60 s — the same engaged rule as lib/engagedTime.ts;
 *   - a row is "viewed" only after MIN_STRETCH_MS (2 s) in ONE unbroken stretch; a shorter glance
 *     sends nothing; once a stretch has qualified, its later seconds are sent too;
 *   - one scheduled batched post per FLUSH_MS (30 s) at most. Two events also send what is owed at
 *     once, so seconds are never credited to the wrong screen or lost: a screen change (the old
 *     screen's figures) and the tab being hidden or closed (keepalive). Nothing is posted when
 *     nothing is owed. Payload: the screen path + tab, and per figure a surface, a product code and whole
 *     seconds. Never a value, never what was typed.
 *   - exports (a CSV download, a copy of a figure, a print) ride the same batch. */

/* The engaged rule's two numbers, the same as lib/engagedTime.ts (IDLE_MS, MAX_TICK_MS). Restated, not
 * imported: the Node test runner needs a '.ts' import that the app's tsconfig refuses. */
export const IDLE_MS = 60_000;
export const MAX_TICK_MS = 2_000;

export interface Screen { path: string; tab: string | null }

export const MIN_STRETCH_MS = 2_000;
export const FLUSH_MS = 30_000;
export const MAX_FIGURES_PER_POST = 300;

export type Surface = 'final_grid' | 'box_a_lines' | 'line_drilldown' | 'psr_queue' | 'worklist' | 'page';
export type ExportKind = 'csv' | 'copy' | 'print';

export interface FigureEnv {
  now: () => number;
  visible: () => boolean;
  focused: () => boolean;
}

export interface ViewedFigure { s: Surface; id: string; sec: number }
export interface ExportedFigure { kind: ExportKind; s: Surface; ids: string[] }
export interface FigureBatch { page: string; tab: string | null; viewed: ViewedFigure[]; exported: ExportedFigure[] }
export type FigurePost = (batch: FigureBatch, final: boolean) => void;

/* enteredAt: when the observer last put the row on screen. start: when the current unbroken counting
 * stretch began (null = no stretch). SPEC-547: the 2 s is measured from that start, never from the
 * previous tick — a row that appeared 100 ms before a tick is credited 100 ms, not the whole tick. */
interface Row { enteredAt: number; start: number | null; qualified: boolean; pendingMs: number }

export function figureKey(s: Surface, id: string): string { return `${s}\u0000${id}`; }

export function createFigureTracker(env: FigureEnv, post: FigurePost) {
  let lastInput = Number.NEGATIVE_INFINITY;
  let lastTick = env.now();
  let lastFlush = env.now();
  /* When the current unbroken run of counting began (null = the last tick did not count). It starts at an
   * input while visible and focused; otherwise (focus or visibility came back between two ticks, at an
   * unknown moment) at the first tick that counts: under one tick undercounted, never overcounted. */
  let countingSince: number | null = null;
  let screen: Screen = { path: '', tab: null };
  const onScreen = new Set<string>();
  const rows = new Map<string, Row>();
  let exports: ExportedFigure[] = [];

  const engaged = (t: number) => env.visible() && env.focused() && t - lastInput <= IDLE_MS;

  function collect(): ViewedFigure[] {
    const out: ViewedFigure[] = [];
    for (const [key, r] of rows) {
      const sec = Math.floor(r.pendingMs / 1000);
      if (sec < 1) {
        if (!onScreen.has(key)) rows.delete(key);  // gone from the screen, nothing owed: forget it
        continue;
      }
      if (out.length >= MAX_FIGURES_PER_POST) break;
      r.pendingMs -= sec * 1000;
      const [s, id] = key.split('\u0000');
      out.push({ s: s as Surface, id, sec });
    }
    return out;
  }

  function flush(final: boolean) {
    lastFlush = env.now();
    if (!screen.path) { exports = []; return; }
    const viewed = collect();
    if (!viewed.length && !exports.length) return;
    const batch: FigureBatch = { page: screen.path, tab: screen.tab, viewed, exported: exports };
    exports = [];
    post(batch, final);
  }

  /** A stretch ends: a row that left the screen, or the person stopped being engaged. */
  function breakStretch(r: Row) { r.start = null; r.qualified = false; }

  return {
    input() {
      lastInput = env.now();
      // An input is the one moment engagement is KNOWN to start: a run can begin here, not at the next tick.
      if (countingSince === null && engaged(lastInput)) countingSince = lastInput;
    },
    /** The IntersectionObserver's verdict for one row. */
    setOnScreen(s: Surface, id: string, visible: boolean) {
      const key = figureKey(s, id);
      if (visible) {
        if (onScreen.has(key)) return;  // already on screen: its stretch goes on
        onScreen.add(key);
        const r = rows.get(key);
        if (r) { r.enteredAt = env.now(); breakStretch(r); }
        else rows.set(key, { enteredAt: env.now(), start: null, qualified: false, pendingMs: 0 });
      } else {
        onScreen.delete(key);
        const r = rows.get(key);
        if (r) breakStretch(r);
      }
    },
    /** Call about once a second. */
    tick() {
      const t = env.now();
      const dt = t - lastTick;
      lastTick = t;
      const counting = dt > 0 && dt <= MAX_TICK_MS && engaged(t);
      if (!counting) countingSince = null;
      else if (countingSince === null) countingSince = t;
      for (const [key, r] of rows) {
        if (!counting || !onScreen.has(key)) { breakStretch(r); continue; }
        if (r.start === null) r.start = Math.max(countingSince as number, r.enteredAt);
        if (r.qualified) r.pendingMs += dt;
        else if (t - r.start >= MIN_STRETCH_MS) { r.qualified = true; r.pendingMs += t - r.start; }
      }
      if (t - lastFlush >= FLUSH_MS) flush(false);
    },
    /** A CSV download, a copy or a print. */
    exported(kind: ExportKind, s: Surface, ids: string[]) {
      exports.push({ kind, s, ids: ids.slice(0, 50) });
    },
    /** A route change: what was counted belongs to the old screen. */
    setScreen(next: Screen) {
      if (next.path === screen.path && next.tab === screen.tab) return;
      flush(false);
      // What is on screen is a DOM fact (the observer keeps it); only the counting restarts. The
      // person's engagement (countingSince) does not change with the screen: a row on screen at the
      // change restarts its stretch from now.
      const t = env.now();
      for (const r of rows.values()) { breakStretch(r); r.pendingMs = 0; r.enteredAt = Math.max(r.enteredAt, t); }
      screen = next;
    },
    /** The tab is being hidden or closed. */
    hide() {
      flush(true);
      countingSince = null;
      for (const r of rows.values()) breakStretch(r);
    },
    /** For tests: figures currently on screen. */
    onScreenIds(): string[] { return [...onScreen].map((k) => k.split('\u0000')[1]); },
  };
}
